/**
 * iOS Safari の自動再生制限の解錠 (audio unlock)。
 *
 * iOS Safari では、音声の play() はユーザー操作 (タップ) の同期文脈でしか
 * 許可されない。発信/応答フローは「タップ → await (トークン取得等) →
 * device.connect → SDK がリモート音声を play()」と進むため、play() の時点
 * では操作文脈が失効しており NotAllowedError で黙って失敗する —
 * 「通話は繋がるのに音声だけ聞こえない」の典型原因。
 * (マイクは権限ダイアログ経由で取得されるため相手には声が届く)
 *
 * 対策: タップ系ハンドラの先頭 (最初の await より前) でこの関数を呼び、
 * 無音 wav を同期的に play() してページの音声再生を解錠しておく。
 * 解錠に成功するまでは呼ばれるたびに再試行する。
 */

// 8kHz 16bit mono 4 サンプルの無音 wav
const SILENT_WAV = 'data:audio/wav;base64,UklGRiwAAABXQVZFZm10IBAAAAABAAEAQB8AAIA+AAACABAAZGF0YQgAAAAAAAAAAAAAAA=='

let unlocked = false
let sharedContext: AudioContext | null = null

export function unlockAudioPlayback(): void {
  if (unlocked) return

  // SDK 内部や着信音が使う AudioContext が suspended のままだと無音になる
  // ブラウザがあるため、共有 context も操作文脈内で resume しておく
  try {
    if (!sharedContext) {
      sharedContext = new AudioContext()
    }
    if (sharedContext.state === 'suspended') {
      void sharedContext.resume()
    }
  } catch {
    // AudioContext 非対応環境では何もしない
  }

  try {
    const el = new Audio(SILENT_WAV)
    el.setAttribute('playsinline', 'true')
    const playPromise = el.play()
    if (playPromise) {
      playPromise
        .then(() => {
          unlocked = true
        })
        .catch(() => {
          // 解錠失敗 (操作文脈外で呼ばれた等) — 次のユーザー操作で再試行
        })
    }
  } catch {
    // no-op
  }
}
